/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useContext } from 'react';
import { Axis, Vertex3 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DimensionLine } from '@r-components';
import { DisplayMode } from '@r-core';
import { DrawPanelBaseDimensions } from './draw-panel-base-dimensions';
import { usePanel } from '@r-hooks';

import { DIMENSION_BREAKPOINT_PADDING_HORIZONTAL, DIMENSION_FONT_SIZE } from '@r-core/constants';
import { type DimensionOptions, DimensionType, ShowDimensions } from '@r-core/types';

export type DrawPanelDimensionsParams = {
	panelId: string;
	isCountertop?: boolean;
	displayMode: DisplayMode;
	xScale?: number;
}

export const DrawPanelDimensions = (params: DrawPanelDimensionsParams): React.ReactElement => {
	const { panelId, isCountertop = false, displayMode, xScale = 1 } = params;

	const { controller } = useContext(AppContext);
	const { options, navigation } = controller;

	const [{ island, panel }, panelMeta] = usePanel({ panelId, isCountertop });

	/* check if there are breakpoints */
	const hasBreakPoints = panelMeta.breakpoints.length > 0;

	/* make some room for the breakpoint dimensions */
	const yBpLineOffset = hasBreakPoints ? 1 : 0;

	/* define some options */
	let { showAppliances, showCountertops } = options;
	let includeLabelBox = true;
	let maxDistanceAboveCountertop = 0;

	/* override options based on display-mode */
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
			showAppliances = false;
			showCountertops = false;
			includeLabelBox = false;
			break;
		default:
			maxDistanceAboveCountertop = panelMeta.maxHeightAbovePanel;
	}

	const frontPanel = island.panels.getFrontPanel();
	const leftPanel = frontPanel.getLeftPanel();
	const rightPanel = frontPanel.getRightPanel();
	const hasLeftPanelConnection: boolean = typeof (island.connections.children.find((connection) => connection.sourcePanelId === leftPanel.id)) !== 'undefined';
	const hasRightPanelConnection: boolean = typeof (island.connections.children.find((connection) => connection.sourcePanelId === rightPanel.id)) !== 'undefined';
	const offset = panelMeta._offsets.countertop;
	const leftOverhang = showCountertops ? leftPanel.countertopOverhang : 0;
	const rightOverhang = showCountertops ? rightPanel.countertopOverhang : 0;
	const { height, width } = showCountertops ? panelMeta.size.outside : panelMeta.size.physical;


	// make adjustments if not showing countertops.
	// if (!showCountertops) {
	// 	panelMeta._size.outside.width -= panelMeta._overhangs.left + panelMeta._overhangs.right;
	// 	offset.x = -(panelMeta._size.outside.width / 2);
	// 	// offset.x += panelMeta._overhangs.left;
	// 	if (isCountertop) {
	// 		panelMeta._size.outside.height -= panelMeta._overhangs.front + panelMeta._overhangs.back;
	// 		offset.y += panelMeta._overhangs.front;
	// 	} else {
	// 		panelMeta._size.outside.height -= panelMeta._countertopThickness;
	// 		offset.y -= panelMeta._offsets.countertop.y;
	// 	}
	// } else if (options.showDimensionsMode === ShowDimensions.Inside && displayMode !== DisplayMode.Overhead) {
	// 	offset.x += leftOverhang;
	// 	offset.y += island.getCountertop().material.thickness;
	// }

	let verticalBreakpoints = [];

	// console.log('DrawPanelDimensions', panelMeta);

	let dimensionOptionsVertical: DimensionOptions | null = null;
	let dimensionOptionsHorizontal: DimensionOptions | null = null;
	let dimensionBaseOptions: DimensionOptions = {
		fontSize: DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor,
		useFractions: options.useFractions,
		indicatorEnd: 'flat',
		indicatorStart: 'flat',
		type: DimensionType.Outside,
		height: 0,
		width: 0,
		xOffset: 0,
		yOffset: 0,
		xScale
	};

	if (island !== null && options.showDimensionsMode !== ShowDimensions.None) {
		if (options.showDimensionsMode === ShowDimensions.Inside) {
			dimensionBaseOptions = {
				...dimensionBaseOptions,
				height: panelMeta.size.physical.height,
				width: panelMeta.size.physical.width,
				xOffset: offset.x,
				yOffset: offset.y
			};
			dimensionOptionsVertical = dimensionBaseOptions;
			dimensionOptionsHorizontal = {
				...dimensionBaseOptions,
				yOffset: dimensionBaseOptions.yOffset
			};
		} else {
			dimensionBaseOptions = {
				...dimensionBaseOptions,
				height: panelMeta.size.outside.height,
				width: panelMeta.size.outside.width,
				xOffset: offset.x,
				yOffset: offset.y
			};

			dimensionOptionsHorizontal = {
				...dimensionBaseOptions,
				yOffset: dimensionBaseOptions.yOffset - (options.showAppliances ? panelMeta.maxHeightAbovePanel : 0)
			};
		}

		if (displayMode === DisplayMode.Overhead && !options.showCountertops) {
			const { islandSize } = island;
			dimensionBaseOptions.height = islandSize.depth;
			dimensionBaseOptions.width = islandSize.width;
		}
		// console.log('panelMeta', DisplayMode.Overhead, panelMeta._island.size, panelMeta._island.orientation, hasLeftPanelConnection, hasRightPanelConnection);

		dimensionOptionsVertical = {
			...dimensionBaseOptions
		};

		if (displayMode === DisplayMode.Overhead || displayMode === DisplayMode.Inline || displayMode === DisplayMode.InlineManufacturing) {
			if (hasLeftPanelConnection && hasRightPanelConnection) {
				dimensionOptionsVertical.xOffset = displayMode === DisplayMode.Inline || displayMode === DisplayMode.InlineManufacturing
					? panel.size.width * 0.90
					: (panelMeta.size.outside.width / 2) - 0.5;
				dimensionOptionsVertical.indicatorEnd = 'arrow';
				dimensionOptionsVertical.indicatorStart = 'arrow';
			} else if (hasLeftPanelConnection) {
				dimensionOptionsVertical.xOffset = panel.size.width + rightOverhang + 6;
			}
		}

		if (hasBreakPoints && options.showBreakpoints) {
			const bpList = panelMeta.breakpoints;
			let prevBpPosition = -(width / 2);
			verticalBreakpoints = bpList.map((bp) => {
				const bpd = {
					...dimensionOptionsHorizontal,
					width: bp.center - prevBpPosition,
					xOffset: prevBpPosition,
					yOffset: dimensionOptionsHorizontal.yOffset
				};
				prevBpPosition += bpd.width;
				return bpd;
			});


			// add end dimension
			verticalBreakpoints.push({
				...dimensionOptionsHorizontal,
				width: (width / 2) - prevBpPosition,
				xOffset: prevBpPosition,
				yOffset: dimensionOptionsHorizontal.yOffset
			});
		}

		return (<>
			{options.showIslandBaseDimensions && <DrawPanelBaseDimensions {...params} />}
			{/* <DimensionLine length={dimensionOptionsVertical.height} position={panelMeta.panel.position}/> */}
			{/* <DrawVerticalDimension dimensionOptions={dimensionOptionsVertical} controller={controller} />
			<DrawHorizonalDimension dimensionOptions={dimensionOptionsHorizontal} controller={controller} /> */}

			{/* breakpoint measurements */}
			{verticalBreakpoints.map((vbp, i) => {
				return <DimensionLine key={i}
					length={vbp.width}
					bBox={{
						origin: new Vertex3({
							dataIn: {
								x: vbp.xOffset + (vbp.width / 2),
								y: -panel.size.height
							}
						}),

						size: { width: vbp.width, height: vbp.height, depth: 0 }
					}}

					axis={new Axis({ dataIn: { x: 0, y: 0, z: 0 }})}
					includeLabelBox={includeLabelBox}
					padding={(showAppliances ? -maxDistanceAboveCountertop : 0) -
						DIMENSION_BREAKPOINT_PADDING_HORIZONTAL -
						(showCountertops || showAppliances ? panelMeta.countertop.material.thickness : 0)
					}
					indicatorStart="flat" indicatorEnd="flat"
					canMove={true}
					level={true}
				/>
			})}
		</>);
	}

	// export type DrawLineProps = {
	// 	length: number;
	// 	origin?: Vertex3;
	// 	axis?: Axis;
	// 	padding?: Vertex3;
	// 	topView?: boolean;
	// 	indicatorStart?: string;
	// 	indicatorEnd?: string;
	// 	labelFactor?: number;
	// 	lineOptions?: Partial<DimensionLineOptions>
	// }

	return <></>;
};
